import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getContrastTextColor } from '../utils/luminance';

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  // Hover text on primary: black or white from luminance (same threshold as
  // --vibe-color-text-on-primary / Footer / HeroSolidBG).
  const tokens = useTokens();
  const ctaHoverTextColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    return getContrastTextColor(primary);
  }, [tokens]);

  return (
    <section
      className="relative w-full min-h-[70vh] md:min-h-[80vh] overflow-hidden flex items-center"
      style={
        ctaHoverTextColor
          ? { ['--hero-cta-hover-text' as string]: ctaHoverTextColor }
          : undefined
      }
    >
      {/* Static full-bleed cover — mirrors Acclaim's CSS background-size:cover
          / background-position:0 0 treatment (no Ken Burns, no parallax). */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        <div className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              priority
              sizes="100vw"
              className="w-full h-full object-cover object-left-top"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Flat dark overlay (~30% opacity) — matches Acclaim's
          hasBackgroundOverlay :before (rgba(40,40,40) @ 0.3). Must not
          intercept clicks so floating widgets remain usable. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundColor: 'rgba(40, 40, 40, 0.3)' }}
        aria-hidden="true"
      />

      {/* Content panel flush to the hero's left edge (no left gutter).
          Pointer events disabled on the outer flex so empty hero space
          stays click-through. */}
      <div className="relative z-10 w-full pl-0 pr-4 sm:pr-6 md:pr-10 lg:pr-12 py-16 md:py-24 lg:py-28 pointer-events-none">
        <div
          className="hero-content-panel hero-entrance hero-entrance-1 max-w-xl md:max-w-2xl pointer-events-auto"
        >
          <h1 className="hero-entrance hero-entrance-2 font-heading text-4xl sm:text-5xl md:text-6xl font-bold mb-8 leading-[1.1] tracking-tight text-white capitalize">
            <RichTextContent
              path="hero.lead"
              label="Lead-in"
              defaultValue="<p></p>"
              as="span"
              className="block"
            />
            <RichTextContent
              path="hero.title"
              label="Title"
              defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
              as="span"
              className="block"
            />
          </h1>

          <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || '/floor-plans'}
              target={ctaLink._target || '_self'}
              className="hero-entrance hero-entrance-3 hero-cta group/cta relative inline-flex items-center gap-2.5 px-8 py-3.5 text-sm font-bold tracking-[0.12em] uppercase overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
              <span className="relative z-10 text-lg leading-none transition-transform duration-300 group-hover/cta:translate-x-0.5" aria-hidden="true">
                ›
              </span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.05s; }
        .hero-entrance-2 { animation-delay: 0.2s; }
        .hero-entrance-3 { animation-delay: 0.35s; }
        /* Flush-left translucent panel with secondary top accent */
        .hero-content-panel {
          background: rgba(33, 34, 34, 0.64);
          border-top: 4px solid var(--vibe-color-secondary);
          padding: 1.75rem 1.75rem 2.25rem 2rem;
          margin-left: 0;
        }
        @media (min-width: 768px) {
          .hero-content-panel {
            padding: 2rem 2rem 2.75rem 3.5rem;
          }
        }
        .hero-cta {
          background: var(--vibe-color-secondary);
          border: none;
          border-radius: 2px;
          color: var(--vibe-color-text-on-secondary, #fff);
          box-shadow: none;
        }
        .hero-cta:hover {
          background: var(--vibe-color-primary);
          color: var(--hero-cta-hover-text, var(--vibe-color-text-on-primary, #fff));
          transform: translateY(-1px);
        }
        .hero-cta:active {
          transform: translateY(0);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.lead': {
      label: 'Lead-in',
      type: 'richtext',
      defaultValue: '<p></p>',
    },
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
